import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/constants";
import { AuthLayout } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui-bits";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); return; }
    api.post("/auth/verify-email", { token })
      .then(async () => { setState("success"); await refresh(); })
      .catch(() => setState("error"));
  }, [token, refresh]);

  const goHome = () => navigate(user ? roleHome(user) : "/login");

  if (state === "verifying") {
    return (
      <AuthLayout title={t("auth.verify.verifyingTitle")} subtitle={t("auth.verify.verifyingSubtitle")}>
        <div data-testid="verify-loading" className="flex items-center gap-3 text-sm text-muted-foreground">
          <Spinner /> {t("common.loading")}
        </div>
      </AuthLayout>
    );
  }

  if (state === "success") {
    return (
      <AuthLayout title={t("auth.verify.successTitle")} subtitle={t("auth.verify.successSubtitle")}>
        <div data-testid="verify-success" className="rounded-lg border border-success/30 bg-success/5 p-4 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-success shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground leading-relaxed">{t("auth.verify.successBody")}</p>
        </div>
        <Button data-testid="verify-continue" onClick={goHome} className="mt-6 w-full h-11 rounded-lg">
          {user ? t("common.goToDashboard") : t("common.signIn")} <ArrowRight size={16} className="ml-1" />
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.verify.errorTitle")} subtitle={t("auth.verify.errorSubtitle")}>
      <div data-testid="verify-error" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
        <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">{t("auth.verify.errorBody")}</p>
      </div>
      <Link to="/login" data-testid="verify-to-login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
        {t("common.signIn")} <ArrowRight size={15} />
      </Link>
    </AuthLayout>
  );
}
